// `configureDictationBubbleUiHost` — the injected-host seam for the web
// dictation bubble, shaped like `ui/widget/host.ts`.
//
// Every member is app UI the bubble subtree renders and cannot import: the CSAT
// thumbs button and form fields, the mic-selector bars, two app hooks, the
// router and the native widget hide. Hooks are called through the host at the
// line they replaced, so hook order is unchanged. The app binds the host in
// `src/composition/hosts/dictation/bubble/dictation-bubble-wiring.tsx`, installed by the
// layout that mounts `DictationBubble`.
import type { ComponentType, RefObject, UIEvent } from 'react';
import type { Control } from 'react-hook-form';

import type { WidgetThumbsButtonProps } from '../widget/widget-states/widget-output-feedback';
import type { DictationCsatFormValues } from './csat/hooks/useDictationCsatForm';

interface DictationBubbleBarsProps {
  isSilent?: boolean;
  className?: string;
}

export interface DictationBubbleUiHost {
  /** The app's CSAT thumbs button, rendered in the output-feedback slot. */
  ThumbsTooltipButton: ComponentType<WidgetThumbsButtonProps>;
  /** The CSAT popover's rating and comment fields. */
  CsatPopoverFormFields: ComponentType<{
    control: Control<DictationCsatFormValues>;
    hasStarSelected: boolean;
    onDismiss: () => void;
  }>;
  /** The web audio-level bars. */
  Bars: ComponentType<DictationBubbleBarsProps & { isMic: boolean; isTab: boolean }>;
  /** The desktop audio-level bars. */
  DesktopBars: ComponentType<DictationBubbleBarsProps>;
  /** The static bars beside the mic picker. */
  BarsBase: ComponentType<{ isSilent?: boolean; audioReady?: boolean }>;
  /** Copies text to the clipboard as HTML and markdown. */
  useCopyToClipboard: () => { copyToClipboard: (text: string) => Promise<boolean> };
  /** Top/bottom scroll gradients for an overflowing element. */
  useScrollGradient: (
    ref: RefObject<HTMLElement | null>,
    deps?: unknown[]
  ) => {
    showTopGradient: boolean;
    showBottomGradient: boolean;
    handleScroll: (event: UIEvent<HTMLElement>) => void;
  };
  /** The current route and a push that settles when the route does. */
  useAppRouter: () => { pathname: string; push: (href: string) => Promise<boolean> };
  /** Hides the native dictation widget window. */
  hideDictationWidget: () => Promise<void>;
}

let host: DictationBubbleUiHost | null = null;

export const configureDictationBubbleUiHost = (next: DictationBubbleUiHost): void => {
  host = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getDictationBubbleUiHost = (): DictationBubbleUiHost => {
  if (!host) {
    throw new Error(
      '[dictate/ui/bubble] UI host not configured — the app must call ' +
        'configureDictationBubbleUiHost() before the dictation bubble renders ' +
        '(see src/composition/hosts/dictation/bubble/dictation-bubble-wiring.tsx, installed ' +
        'by scribe-layout); tests rendering this surface must configure a test ' +
        'double first.'
    );
  }
  return host;
};

/** Test-only: drops the registered host so a suite can assert the failure. */
export const __resetDictationBubbleUiHostForTests = (): void => {
  host = null;
};
